/**
 * @fileoverview GET handler for the "/custom_objects/:type_name/schema/fields" endpoint.
 * The backend returns a paginated envelope (`items` + `total_count`); this adapts it to the
 * dashboard DataTable shape (`results` + `hits`).
 */
import fetch from "@lib/fetch";

import { reactQueryTableFetcherAdapter } from "../reactQueryFetcherAdapters";
import { customObjectsTypeNameSchemaFields } from "./constants";
import type {
  CustomObjectsTypeNameSchemaFieldsParams,
  CustomObjectsTypeNameSchemaFieldsResult,
  RawCustomObjectsTypeNameSchemaFieldsResponse,
} from "./types";
import { isCustomObjectsTypeNameSchemaFieldsResult } from "./validators";

export const toCustomObjectsTypeNameSchemaFieldsTableResult = (
  response: RawCustomObjectsTypeNameSchemaFieldsResponse
): CustomObjectsTypeNameSchemaFieldsResult => {
  const items = response.items ?? [];

  return {
    hits: Math.max(response.total_count ?? 0, items.length),
    results: items.map((item) => ({
      id: item.name,
      name: item.name,
      display_label: item.display_label ?? item.name,
      data_type: item.data_type ?? "",
      required: item.required ?? false,
      position: item.position ?? 0,
    })),
  };
};

export const getDataTableCustomObjectsTypeNameSchemaFields = reactQueryTableFetcherAdapter(
  customObjectsTypeNameSchemaFields,
  (
    signal: AbortSignal,
    { appGroupId, typeName, limit = 12, start = 0 }: CustomObjectsTypeNameSchemaFieldsParams
  ): Promise<CustomObjectsTypeNameSchemaFieldsResult> => {
    return fetch<RawCustomObjectsTypeNameSchemaFieldsResponse>(
      `/custom_objects/${encodeURIComponent(typeName)}/schema/fields`,
      "GET",
      {
        app_group_id: appGroupId,
        limit,
        offset: start,
      },
      { signal },
      { propagateFetchRequestError: false }
    ).then(toCustomObjectsTypeNameSchemaFieldsTableResult);
  },
  isCustomObjectsTypeNameSchemaFieldsResult
);
